import React from 'react';
import { Alert, Button, Col, Form, FormField, FormRow, formWithValidation, Right, Row } from '@edgebox/react-components';
import { type IOptionalSerializedReference, InternalId } from '@edgebox/data-definition-kit';
import {
  AccessScope,
  AiProviderApiType,
  AiProviderAuthenticationType,
  AiProviderStatus,
  type ISyncCoreAiProviderDTO,
  type ISyncCoreSerializedAiProviderProperties,
} from '@edgebox/sync-core-data-definitions';
import type { ISyncCoreApi } from '@edgebox/sync-core-rest-client';
import { ClientAiProviderDraft, ClientAiProviderEntity } from '@edgebox/sync-core-rest-client';
import { instanceToPlain, plainToInstance } from 'class-transformer';
import { enumValues, normalizeFilterId } from '../aiHelpers';

const ProviderDraftForm = formWithValidation<ISyncCoreSerializedAiProviderProperties, ClientAiProviderDraft>(
  (values) => plainToInstance(ClientAiProviderDraft, values),
  (entity) => instanceToPlain(entity) as ISyncCoreSerializedAiProviderProperties
);

const ProviderEntityForm = formWithValidation<ISyncCoreAiProviderDTO, ClientAiProviderEntity>(
  (values) => plainToInstance(ClientAiProviderEntity, values),
  (entity) => instanceToPlain(entity) as ISyncCoreAiProviderDTO
);

function IdReferenceField({ label, value, onChange }: { label: string; value: string; onChange: (next: string) => void }) {
  return (
    <Row className="mb-3">
      <Col>
        <Form.Label className="fw-bold">{label}</Form.Label>
        <Form.Control value={value} onChange={(e) => onChange(e.target.value)} placeholder="Optional" />
      </Col>
    </Row>
  );
}

function optionalSerializedReference(id?: InternalId): IOptionalSerializedReference | undefined {
  if (!id) return undefined;
  return { _: 1, id };
}

function getSubmitErrorMessage(error: unknown): string {
  if (!error) return '';

  const responseData = (error as any)?.response?.data;
  const payload = responseData ?? error;

  if (payload && typeof payload === 'object') {
    const message = (payload as any).message;
    const statusCode = (payload as any).statusCode;
    const errorLabel = (payload as any).error;

    if (Array.isArray(message)) {
      const lines = message
        .map((m) => {
          const property = (m as any)?.property;
          const constraints = (m as any)?.constraints;
          const constraintText = constraints && typeof constraints === 'object' ? Object.values(constraints).join(' ') : undefined;
          if (property && constraintText) return `${property}: ${constraintText}`;
          if (property) return String(property);
          return typeof m === 'string' ? m : JSON.stringify(m);
        })
        .filter(Boolean);

      const header = [statusCode ? String(statusCode) : undefined, errorLabel ? String(errorLabel) : undefined].filter(Boolean).join(' ');
      return `${header || 'Request failed'}\n${lines.join('\n')}`;
    }

    if (typeof message === 'string' && message.length) return message;
  }

  return error instanceof Error ? error.message : String(error);
}

export function AiProviderDraftEditor({
  api,
  initialValues,
  submitLabel,
  onSubmit,
}: {
  api: ISyncCoreApi;
  initialValues: ClientAiProviderDraft;
  submitLabel: string;
  onSubmit: (draft: ClientAiProviderDraft) => Promise<void>;
}) {
  const initialCustomerId = initialValues.customer?.getId?.() ?? '';
  const initialProjectId = initialValues.project?.getId?.() ?? '';

  const [saving, setSaving] = React.useState(false);
  const [submitError, setSubmitError] = React.useState<unknown>(undefined);

  return (
    <ProviderDraftForm
      initialValues={initialValues}
      onSubmit={async (draft) => {
        setSaving(true);
        setSubmitError(undefined);
        try {
          await onSubmit(draft);
        } catch (e) {
          setSubmitError(e);
        } finally {
          setSaving(false);
        }
      }}
    >
      {({ values, setValue }) => {
        const currentCustomerId = values.customer?.id ?? '';
        const currentProjectId = values.project?.id ?? '';

        return (
          <>
            {submitError ? (
              <Alert variant="danger" className="mb-3">
                <div className="fw-bold">Save failed</div>
                <pre className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                  {getSubmitErrorMessage(submitError)}
                </pre>
              </Alert>
            ) : null}
            <Row>
              <Col md={6}>
                <FormRow name={'name'} label={'Name'} labelPlacement={'above'} required>
                  <FormField name={'name'} type={'text'} noRow />
                </FormRow>
              </Col>

              <Col md={3}>
                <FormRow name={'status'} label={'Status'} labelPlacement={'above'} required>
                  <Form.Select value={values.status} onChange={(e) => setValue('status', e.target.value as AiProviderStatus)}>
                    {enumValues(AiProviderStatus).map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </Form.Select>
                </FormRow>
              </Col>

              <Col md={3}>
                <FormRow name={'accessScope'} label={'Access scope'} labelPlacement={'above'} required>
                  <Form.Select value={values.accessScope} onChange={(e) => setValue('accessScope', e.target.value as AccessScope)}>
                    {enumValues(AccessScope).map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </Form.Select>
                </FormRow>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <FormRow name={'apiType'} label={'API type'} labelPlacement={'above'} required>
                  <Form.Select value={values.apiType} onChange={(e) => setValue('apiType', e.target.value as AiProviderApiType)}>
                    {enumValues(AiProviderApiType).map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </Form.Select>
                </FormRow>
              </Col>

              <Col md={4}>
                <FormRow name={'authenticationType'} label={'Authentication'} labelPlacement={'above'} required>
                  <Form.Select
                    value={values.authenticationType}
                    onChange={(e) => setValue('authenticationType', e.target.value as AiProviderAuthenticationType)}
                  >
                    {enumValues(AiProviderAuthenticationType).map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </Form.Select>
                </FormRow>
              </Col>

              <Col md={4}>
                <FormRow name={'frozen'} label={'Frozen'} labelPlacement={'above'}>
                  <Form.Check type="checkbox" label="" checked={values.frozen} onChange={(e) => setValue('frozen', e.target.checked)} />
                </FormRow>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <FormRow name={'baseUrl'} label={'Base URL'} labelPlacement={'above'}>
                  <FormField name={'baseUrl'} type={'text'} noRow />
                </FormRow>
              </Col>
              <Col md={3}>
                <FormRow name={'region'} label={'Region'} labelPlacement={'above'}>
                  <FormField name={'region'} type={'text'} noRow />
                </FormRow>
              </Col>
              <Col md={3}>
                <FormRow name={'apiVersion'} label={'API version'} labelPlacement={'above'}>
                  <FormField name={'apiVersion'} type={'text'} noRow />
                </FormRow>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <FormRow name={'defaultTimeoutMs'} label={'Default timeout'} labelPlacement={'above'}>
                  <FormField name={'defaultTimeoutMs'} type={'number'} suffix="ms" noRow />
                </FormRow>
              </Col>
              <Col md={6}>
                <FormRow name={'maxTimeoutMs'} label={'Max timeout'} labelPlacement={'above'}>
                  <FormField name={'maxTimeoutMs'} type={'number'} suffix="ms" noRow />
                </FormRow>
              </Col>
            </Row>

            <IdReferenceField
              label="Customer ID"
              value={currentCustomerId || initialCustomerId}
              onChange={(next) => {
                const id = normalizeFilterId(next);
                setValue('customer', optionalSerializedReference(id));
              }}
            />

            <IdReferenceField
              label="Project ID"
              value={currentProjectId || initialProjectId}
              onChange={(next) => {
                const id = normalizeFilterId(next);
                setValue('project', optionalSerializedReference(id));
              }}
            />

            <Right className="pe-1">
              <Button variant="primary" type="submit" disabled={saving}>
                {submitLabel}
              </Button>
            </Right>
          </>
        );
      }}
    </ProviderDraftForm>
  );
}

export function AiProviderEntityEditor({
  api,
  initialValues,
  submitLabel,
  onSubmit,
}: {
  api: ISyncCoreApi;
  initialValues: ClientAiProviderEntity;
  submitLabel: string;
  onSubmit: (entity: ClientAiProviderEntity) => Promise<void>;
}) {
  const [saving, setSaving] = React.useState(false);
  const [submitError, setSubmitError] = React.useState<unknown>(undefined);

  const initialCustomerId = initialValues.customer?.getId?.() ?? '';
  const initialProjectId = initialValues.project?.getId?.() ?? '';

  return (
    <ProviderEntityForm
      initialValues={initialValues}
      onSubmit={async (entity) => {
        setSaving(true);
        setSubmitError(undefined);
        try {
          await onSubmit(entity);
        } catch (e) {
          setSubmitError(e);
        } finally {
          setSaving(false);
        }
      }}
    >
      {({ values, setValue }) => {
        const currentCustomerId = values.customer?.id ?? '';
        const currentProjectId = values.project?.id ?? '';

        return (
          <>
            {submitError ? (
              <Alert variant="danger" className="mb-3">
                <div className="fw-bold">Save failed</div>
                <pre className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                  {getSubmitErrorMessage(submitError)}
                </pre>
              </Alert>
            ) : null}
            <Row>
              <Col md={6}>
                <FormRow name={'name'} label={'Name'} labelPlacement={'above'} required>
                  <FormField name={'name'} type={'text'} noRow />
                </FormRow>
              </Col>

              <Col md={3}>
                <FormRow name={'status'} label={'Status'} labelPlacement={'above'} required>
                  <Form.Select value={values.status} onChange={(e) => setValue('status', e.target.value as AiProviderStatus)}>
                    {enumValues(AiProviderStatus).map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </Form.Select>
                </FormRow>
              </Col>

              <Col md={3}>
                <FormRow name={'accessScope'} label={'Access scope'} labelPlacement={'above'} required>
                  <Form.Select value={values.accessScope} onChange={(e) => setValue('accessScope', e.target.value as AccessScope)}>
                    {enumValues(AccessScope).map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </Form.Select>
                </FormRow>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <FormRow name={'apiType'} label={'API type'} labelPlacement={'above'} required>
                  <Form.Select value={values.apiType} onChange={(e) => setValue('apiType', e.target.value as AiProviderApiType)}>
                    {enumValues(AiProviderApiType).map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </Form.Select>
                </FormRow>
              </Col>

              <Col md={4}>
                <FormRow name={'authenticationType'} label={'Authentication'} labelPlacement={'above'} required>
                  <Form.Select
                    value={values.authenticationType}
                    onChange={(e) => setValue('authenticationType', e.target.value as AiProviderAuthenticationType)}
                  >
                    {enumValues(AiProviderAuthenticationType).map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </Form.Select>
                </FormRow>
              </Col>

              <Col md={4}>
                <FormRow name={'frozen'} label={'Frozen'} labelPlacement={'above'}>
                  <Form.Check type="checkbox" label="" checked={values.frozen} onChange={(e) => setValue('frozen', e.target.checked)} />
                </FormRow>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <FormRow name={'baseUrl'} label={'Base URL'} labelPlacement={'above'}>
                  <FormField name={'baseUrl'} type={'text'} noRow />
                </FormRow>
              </Col>
              <Col md={3}>
                <FormRow name={'region'} label={'Region'} labelPlacement={'above'}>
                  <FormField name={'region'} type={'text'} noRow />
                </FormRow>
              </Col>
              <Col md={3}>
                <FormRow name={'apiVersion'} label={'API version'} labelPlacement={'above'}>
                  <FormField name={'apiVersion'} type={'text'} noRow />
                </FormRow>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <FormRow name={'defaultTimeoutMs'} label={'Default timeout'} labelPlacement={'above'}>
                  <FormField name={'defaultTimeoutMs'} type={'number'} suffix="ms" noRow />
                </FormRow>
              </Col>
              <Col md={6}>
                <FormRow name={'maxTimeoutMs'} label={'Max timeout'} labelPlacement={'above'}>
                  <FormField name={'maxTimeoutMs'} type={'number'} suffix="ms" noRow />
                </FormRow>
              </Col>
            </Row>

            <IdReferenceField
              label="Customer ID"
              value={currentCustomerId || initialCustomerId}
              onChange={(next) => {
                const id = normalizeFilterId(next);
                setValue('customer', optionalSerializedReference(id));
              }}
            />

            <IdReferenceField
              label="Project ID"
              value={currentProjectId || initialProjectId}
              onChange={(next) => {
                const id = normalizeFilterId(next);
                setValue('project', optionalSerializedReference(id));
              }}
            />

            <Right className="pe-1">
              <Button variant="primary" type="submit" disabled={saving}>
                {submitLabel}
              </Button>
            </Right>
          </>
        );
      }}
    </ProviderEntityForm>
  );
}
